import {
  Accordion,
  Alert,
  Box,
  ContentBlock,
  Heading,
  Stack,
  Strong,
  Text,
  AccordionItem,
  List,
  TextLink,
} from 'braid-design-system';

import { Nav } from 'src/components/Nav/Nav';
import { PageWrapper } from 'src/components/PageWrapper/PageWrapper';
import type { PageProps } from 'src/types';
import { useLocalized } from 'src/utils/hooks/useLocalized';
import { slugify } from 'src/utils/slugify';

export const TermsOfUse = ({ slug }: PageProps) => {
  const pageTrackingProps = {
    page: `${slugify({ str: slug })}`,
    eventName: `${slugify({ str: slug })}-viewed`,
    siteSection: 'partners',
  };

  return (
    <PageWrapper pageTrackingProps={pageTrackingProps} title={slug}>
      <Nav activeTab="terms-of-use" />
      <Box
        paddingY="xlarge"
        paddingBottom="xxlarge"
        paddingX={['gutter', 'none']}
      >
        <ContentBlock>
          <Stack space="xlarge">
            <Box>
              <Heading level="1">SEEK API Terms of Use</Heading>
            </Box>
            <Stack space="large">
              <Stack space="gutter">
                <Alert tone="info">
                  <Stack space="medium">
                    <Text>
                      <em>Last Updated 30 Jan 2025.</em>
                    </Text>
                  </Stack>
                </Alert>
                <Text>
                  These SEEK API Terms of Use (<Strong>Terms</Strong>) govern
                  the use of the SEEK API (as defined below). By accessing and
                  using the SEEK API, you are agreeing to enter into a legally
                  binding contract in consideration of the mutual promises
                  contained in these Terms.
                </Text>
              </Stack>
              <Accordion>
                <AccordionItem label="Definitions" id="definitions">
                  <List type="number">
                    <Stack space="medium">
                      <Text>In these Terms:</Text>
                      <List type="alpha">
                        <Text>
                          <Strong>Advertiser</Strong> means a party that has
                          placed (or intends to place) an advertisement on our
                          websites and apps.
                        </Text>
                        <Text>
                          <Strong>Candidate</Strong> means an individual using
                          our websites or apps for job seeking purposes and/or
                          responding to a job advertisement posted on our
                          websites or apps.
                        </Text>
                        <Text>
                          <Strong>Data</Strong> refers to any information
                          sourced or accessed by your Recruitment Software via
                          the SEEK API including, without limitation,
                          Candidates&apos; job applications, Profiles and
                          Personal Information.
                        </Text>
                        <Text>
                          <Strong>Detrimental Change</Strong> has the meaning
                          given in clause 2b) of these Terms.
                        </Text>
                        <Text>
                          <Strong>Mutual Customers</Strong> means an Advertiser
                          that uses your Recruitment Software to assist with
                          their recruitment activities through our websites and
                          apps.
                        </Text>
                        <Text>
                          <Strong>Notice Period</Strong> has the meaning given
                          in clause 2b) of these Terms.
                        </Text>
                        <Text>
                          <Strong>our websites and apps</Strong> means our SEEK,
                          Jobstreet by SEEK (excluding Jobstreet Express) and
                          Jobsdb by SEEK employment websites and apps.
                        </Text>
                        <Text>
                          <Strong>Partner Hub</Strong> means
                          talent.seek.com.au/partners.
                        </Text>
                        <Text>
                          <Strong>Personal Information</Strong> is defined as
                          “personal information” or “personal data” by
                          applicable Privacy Laws or regulations relating to the
                          collection, use or storage of opinion or information
                          that identifies or could reasonably identify an
                          individual. This includes facts and opinions, whether
                          they are true or not.
                        </Text>
                        <Text>
                          <Strong>Privacy Laws</Strong> means the applicable
                          privacy and data protection laws as may be in force
                          from time to time which regulate the collection, use,
                          disclosure, storage of and granting of access rights
                          to Personal Information.
                        </Text>
                        <Text>
                          <Strong>Profile</Strong> means a SEEK Profile,
                          Jobstreet by SEEK Profile, or Jobsdb by SEEK Profile
                          (where applicable) page where Candidates create
                          personal profiles on our websites and apps by
                          inputting their Personal Information.
                        </Text>
                        <Text>
                          <Strong>Recruitment Software</Strong> means a software
                          application used and/or developed by you for the
                          purposes of integrating with the SEEK API.
                        </Text>
                        <Text>
                          <Strong>SEEK API</Strong> means an Application
                          Programming Interface that provides you programmatic
                          access to our websites and apps.
                        </Text>
                        <Text>
                          <Strong>SEEK Group</Strong> means SEEK Limited and its
                          related bodies corporate, as defined in the Australian
                          Corporations Act 2001 (Cth). References to “we” and
                          “us” (and related terms) means the SEEK Group unless
                          otherwise stated.
                        </Text>
                        <Text>
                          <Strong>SEEK Group Branding</Strong> means the logos,
                          trade marks, business name and domain names owned by a
                          member of the SEEK Group that are provided to you.
                        </Text>
                        <Text>
                          <Strong>SEEK Developer Site</Strong> means
                          developer.seek.com and/or devportal.seek.com.au,
                          whichever is applicable to you.
                        </Text>
                        <Text>
                          <Strong>Talent Search Connect</Strong> means the
                          Talent Search Connect product which we may offer to
                          Advertisers.
                        </Text>
                        <Text>
                          <Strong>Term</Strong> has the meaning given to in in
                          clause 49 of these Terms.
                        </Text>
                      </List>
                    </Stack>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="variation-of-terms"
                  label="Variation of Terms"
                >
                  <List type="number" start={2}>
                    <Stack space="medium">
                      <Text />
                      <List type="alpha">
                        <Text>
                          Subject to clauses 2b) and 2c), we may vary these
                          Terms from time to time and the updated version will
                          apply to you. Our current terms are accessible through
                          the SEEK Developer Site.
                        </Text>
                        <Text>
                          You will be notified of any changes to these Terms
                          which we reasonably believe will be materially
                          detrimental to you (
                          <Strong>Detrimental Change</Strong>) 30 days prior to
                          them taking effect (<Strong>Notice Period</Strong>).
                        </Text>
                        <Text>
                          In the event of a proposed Detrimental Change, you may
                          elect to terminate the relevant aspect of your
                          agreement with us during the Notice Period by
                          providing at least 5 days&apos; written notice. For
                          the avoidance of doubt, your agreement with us in
                          relation to products and services not subject to the
                          Detrimental Change will otherwise remain on foot (to
                          the extent possible).
                        </Text>
                      </List>
                    </Stack>
                  </List>
                </AccordionItem>

                <AccordionItem id="integration" label="Integration">
                  <List type="number" start={3}>
                    <Text>
                      To integrate with the SEEK API, you must submit a request
                      via our Integration form{' '}
                      <TextLink href="/partners/integration-request/">
                        here
                      </TextLink>{' '}
                      and meet our criteria for approval.
                    </Text>
                    <Stack space="medium">
                      <Text>
                        As part of your integration request, you will be
                        required to provide the following information (in
                        addition to any other information we may require from
                        time to time):
                      </Text>
                      <List type="alpha">
                        <Text>the name of your company;</Text>
                        <Text>
                          an e-mail address and phone number of the primary
                          contact person; and
                        </Text>
                        <Text>
                          such other information may be reasonably requested by
                          us, (collectively, your{' '}
                          <Strong>Identification Details</Strong>).
                        </Text>
                      </List>
                    </Stack>
                    <Text>
                      It is your responsibility to ensure that your
                      Identification Details always remain current.
                    </Text>
                    <Text>
                      Once you have been approved for development, we will
                      provide you with the necessary partner credentials.
                    </Text>
                    <Text>
                      We do not guarantee that we will consent to the use and/or
                      integration of your Recruitment Software. We may condition
                      our consent to the use of your Recruitment Software on any
                      number of factors including a modification of your
                      Recruitment Software to a format or system more compatible
                      with the SEEK API. We may work with you to achieve such an
                      outcome, although we are under no obligation to do so.
                    </Text>
                    <Text>
                      If you decide to terminate your use of the SEEK API in
                      accordance with clause 53 of these Terms, your credentials
                      will be terminated.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem id="implementation" label="Implementation">
                  <List type="number" start={9}>
                    <Stack space="medium">
                      <Text>
                        In the event that we consent to your integration with
                        the SEEK API, you agree to work collaboratively with us
                        to undertake such integration including to:
                      </Text>
                      <List type="alpha">
                        <Text>
                          provide us with an in-system demo overview of your
                          current integrations with us (if applicable);
                        </Text>
                        <Text>
                          complete system integration and/or integration
                          verification testing of all required test cases; and
                        </Text>
                        <Text>
                          provide visibility of your SEEK API integrations for
                          the purpose of system integration and/or integration
                          verification testing activities.
                        </Text>
                      </List>
                    </Stack>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="intellectual-property"
                  label="Intellectual Property"
                >
                  <List type="number" start={10}>
                    <Text>
                      Except to the limited extent expressly provided in these
                      Terms, SEEK Limited owns all intellectual property rights,
                      title, and interest in and to the SEEK API, including all
                      elements and components of the SEEK API.
                    </Text>
                    <Text>
                      Subject to these Terms, SEEK Limited grants you a limited,
                      non-exclusive, non-assignable and non-transferable licence
                      to use the SEEK API to develop, test, and/or support your
                      Recruitment Software (<Strong>Licence</Strong>) for the
                      duration of the Term, and to allow Mutual Customers that
                      have engaged you to have the benefit of the integration of
                      your Recruitment Software with the SEEK API.
                    </Text>
                    <Text>
                      As between the parties, you own all intellectual property
                      rights, title, and interest in and to the Recruitment
                      Software.
                    </Text>
                    <Text>
                      During the Term, you grant to the SEEK Group a
                      royalty-free, worldwide, non-exclusive, irrevocable right
                      and licence to your intellectual property rights in order
                      to connect Mutual Customers to your Recruitment Software
                      and to verify your compliance with these Terms.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem id="branding" label="Branding">
                  <List type="number" start={14}>
                    <Text>
                      Subject to these Terms, the relevant members of the SEEK
                      Group grant you a limited, non-exclusive, non-assignable,
                      non-transferable and non-sublicensable licence (
                      <Strong>Brand Licence</Strong>) solely for the purpose of
                      displaying the SEEK Group Branding within your Recruitment
                      Software for the duration of the Term.
                    </Text>
                    <Text>
                      For any use of the SEEK Group Branding other than provided
                      for under the Brand Licence, you must obtain our prior
                      written approval before such use.
                    </Text>
                    <Text>
                      You must use the SEEK Group Branding in accordance with
                      the SEEK Group Branding Guidelines, which will be provided
                      to you.
                    </Text>
                    <Text>
                      You grant the SEEK Group a limited, non-exclusive,
                      non-assignable, non-transferable and non-sublicensable
                      licence during the Term to display your logos, trade
                      marks, business name and domain names to advertise your
                      Recruitment Software or your use of the SEEK API on our
                      websites and apps.
                    </Text>
                    <Text>
                      The SEEK Group may publish your brand (name and logo) on
                      our websites and apps, in promotional materials including
                      press releases without additional consent or notice to
                      you.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="seek-api-prohibitions"
                  label="SEEK API Prohibitions"
                >
                  <List type="number" start={19}>
                    <Stack space="medium">
                      <Text>You must not:</Text>
                      <List type="alpha">
                        <Text>
                          require a Candidate to provide any of their details to
                          you, including Personal Information or otherwise
                          create an account with you, as a precondition to you
                          passing on Data in respect of that Candidate to a
                          Mutual Customer;
                        </Text>
                        <Text>
                          sub-license the SEEK API for use by a third party;
                        </Text>
                        <Text>
                          perform any action of introducing any viruses, worms,
                          defects, Trojan horses, malware or any items of a
                          destructive nature to the SEEK APIs;
                        </Text>
                        <Text>
                          interfere with or disrupt the SEEK API or the servers
                          or networks providing the SEEK API; or
                        </Text>
                        <Text>
                          reverse engineer or attempt to extract the source code
                          from the SEEK API or any related software, except to
                          the extent that this restriction is expressly
                          prohibited by applicable law.
                        </Text>
                      </List>
                    </Stack>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="confidential-information"
                  label="Confidential Information"
                >
                  <List type="number" start={20}>
                    <Text>
                      Communications between you and the SEEK Group should be
                      regarded as confidential unless we expressly provide
                      otherwise. You may only use such confidential information
                      for the purposes of performing your obligations or
                      exercising your rights under these Terms. You must not
                      disclose confidential information to any third party
                      without our prior written consent. If you are unsure about
                      whether any information that is communicated to you is
                      confidential in nature, you should treat that information
                      as confidential and confirm with us prior to disclosure of
                      that information.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="compliance"
                  label="Compliance with Law and Terms"
                >
                  <List type="number" start={21}>
                    <Text>
                      You must comply with all applicable laws and these Terms.
                      To the extent that they are applicable, your Recruitment
                      Software must also comply with the{' '}
                      <TextLink href="/support/termsconditions">
                        Advertising Terms of Use
                      </TextLink>
                      .
                    </Text>
                    <Text>
                      In the event of any conflict between the content in these
                      Terms and the above documents in relation to the use of
                      the SEEK API, these Terms will apply.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem id="data-security" label="Data Security">
                  <List type="number" start={23}>
                    <Text>
                      Your network, operating system and the software of your
                      web servers, databases, and computer systems
                      (collectively, <Strong>Systems</Strong>) must be
                      configured to securely operate your Recruitment
                      Software.{' '}
                    </Text>
                    <Text>
                      You must take reasonable steps to protect the Data from
                      misuse, interference, loss and unauthorised access,
                      modification, unlawful use or disclosure, accidental loss
                      or destruction, or damage and will secure the Data and
                      maintain effective security controls that adhere to the
                      standards required by Privacy Laws in order to ensure the
                      safe storage and proper use of the Data.
                    </Text>
                    <Text>
                      If you become aware or suspect that there has been any
                      security deficiencies, vulnerabilities, intrusions, in, or
                      breaches of, your Systems, or unauthorised access to,
                      unauthorised disclosure of, or loss of Data (each, an{' '}
                      <Strong>Incident</Strong>), you must immediately contact
                      us and disclose all information relevant to that actual or
                      suspected Incident via our Contact Us form on the Partner
                      Hub at{' '}
                      <TextLink href="/contactus">
                        {useLocalized({
                          AU: 'https://talent.seek.com.au/contactus',
                          NZ: 'https://talent.seek.co.nz/contactus',
                        })}
                      </TextLink>
                      . You must cooperate with us in investigating and to
                      immediately correct any Incident.
                    </Text>
                    <Text>
                      In the event of an Incident, we reserve the right to
                      temporarily, or permanently reduce or restrict your access
                      to the SEEK API until the Incident has been remediated.
                    </Text>
                    <Text>
                      In the event of any Incident involving your Recruitment
                      Software and the SEEK API, you must not make any public
                      statements in relation to the incident without prior
                      written and express permission from us. You must assist us
                      in verifying your compliance with our security
                      requirements by providing any information about your
                      Recruitment Software and/or Systems to us, which may also
                      include access to your Recruitment Software and other
                      materials related to your use of the Recruitment Software.
                    </Text>
                    <Text>
                      If we become aware of any fraudulent job applications
                      within your Systems, we will notify you. You must work
                      with us to immediately delete any fraudulent applications
                      from your Systems.
                    </Text>
                    <Text>
                      If you become aware of any fraudulent or illegal activity
                      involving a user within your Systems, you must take steps
                      to suspend or terminate that user’s ability to access any
                      of our websites and apps through your Recruitment
                      Software.
                    </Text>
                    <Text>
                      On termination or expiry of these Terms for whatever
                      reason, or upon our written request at any time, cease and
                      ensure that you and your personnel cease to use or process
                      the Data and destroy and/or return and/or procure the
                      return of any and all Data in their possession or control
                      to us.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem id="your-obligations" label="Your Obligations">
                  <List type="number" start={31}>
                    <Stack space="medium">
                      <Text>
                        In addition to the warranties outlined in our{' '}
                        <TextLink href="/support/termsconditions">
                          Advertiser Terms of Use
                        </TextLink>
                        , you warrant that:
                      </Text>
                      <List type="alpha">
                        <Stack space="medium">
                          <Text>
                            your Systems that processes or stores our non-public
                            data and Data:
                          </Text>
                          <List type="roman">
                            <Text>
                              will encrypt data transmitted over a public
                              network;
                            </Text>
                            <Text>will encrypt data at rest;</Text>
                            <Text>
                              has enforced security monitoring controls to
                              detect indicators of security incidents;
                            </Text>
                            <Text>
                              has a documented process for responding to
                              Incidents;
                            </Text>
                            <Text>
                              has documented operational management procedures
                              to ensure that necessary security controls are in
                              place (antivirus, data loss prevention, etc.) and
                              patches are applied in a timely manner;
                            </Text>
                            <Text>
                              will document a provisioning and de-provisioning
                              process for internal business users;
                            </Text>
                            <Text>
                              will enforce controls like single sign-on and
                              multi-factor authentication for internal business
                              users;
                            </Text>
                            <Text>
                              will generate and retain audit logs; and
                            </Text>
                            <Text>
                              is covered by a documented Business Continuity
                              Plan;
                            </Text>
                          </List>
                        </Stack>
                        <Stack space="medium">
                          <Text>
                            you will maintain security controls to reasonably
                            secure your Recruitment Software and to protect the
                            Data from misuse, interference, loss and
                            unauthorised access, modification or disclosure,
                            including, without limitation to:
                          </Text>
                          <List type="roman">
                            <Text>
                              the use of anti-malware software on Systems
                              providing or supporting your Recruitment Software;
                            </Text>
                            <Text>
                              the use of firewalls and related security controls
                              to restrict unauthorised access to your
                              Recruitment Software and the Data from the
                              Internet and other untrusted networks connected to
                              the Systems;
                            </Text>
                            <Text>
                              the regular maintenance and updating of security
                              controls for your Systems;
                            </Text>
                            <Text>
                              the regular monitoring of your Systems for any
                              event that may compromise the security of your
                              Systems, your Recruitment Software or the Data;
                              and
                            </Text>
                            <Text>the regular backup of your Systems;</Text>
                          </List>
                        </Stack>
                        <Text>
                          you, and your Recruitment Software complies, in all
                          material aspects, with the requirements of the
                          Australian Spam Act 2003 (Cth) and all other relevant
                          or similar direct marketing laws that may be
                          applicable to you;
                        </Text>
                        <Text>
                          your Recruitment Software does not breach the
                          intellectual property rights of any third party;
                        </Text>
                        <Text>
                          you will not act for, or on behalf of, an Advertiser
                          without their express written consent;
                        </Text>
                        <Text>
                          you have no proprietary right, title to, or interest
                          in the Data, and you will not use the Data for any
                          purpose other than the transfer of said Data to the
                          Advertiser that has engaged you;
                        </Text>
                        <Text>
                          you will not copy or store any Data for any purpose
                          other than the transfer of said Data to the Advertiser
                          that has engaged you;
                        </Text>
                        <Text>
                          when transmitting Data to a Mutual Customers you will
                          not alter, modify or amend the Data in any material
                          way (this obligation does not restrict you however
                          from sorting the Data in accordance with the
                          instructions of the Mutual Customer);
                        </Text>
                        <Text>
                          you will not develop your Recruitment Software, or use
                          the SEEK API or Data in a way that may harm the
                          professional reputation or relationships of the
                          Advertisers and Candidates; and
                        </Text>
                        <Text>
                          you will not use, distribute, or transfer the SEEK API
                          or Data for any illegal purposes, or in any manner
                          which would violate these Terms, or expose the SEEK
                          Group or its Advertisers and Candidates to legal
                          liability.
                        </Text>
                      </List>
                    </Stack>
                    <Text>
                      You may not assign or transfer any rights and obligations
                      pursuant to these Terms to any other person or entity
                      without our prior written approval (which will not be
                      unreasonably withheld). If you are a company, any change
                      in your effective control shall be deemed an assignment
                      requiring our consent under the terms of this clause.
                    </Text>
                    <Text>
                      You agree to assist us in verifying your compliance with
                      obligations under these Terms by providing any requested
                      information to us that is reasonably required to ascertain
                      your compliance.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem id="privacy" label="Privacy">
                  <List type="number" start={34}>
                    <Stack space="medium">
                      <Text>
                        You must in respect of any Personal Information which
                        you receive or learn via your use of the SEEK API:
                      </Text>
                      <List type="alpha">
                        <Text>
                          not use, disclose and store the Personal Information
                          for any purpose other than for the purpose of carrying
                          out the services that a Mutual Customer has engaged
                          you for;
                        </Text>
                        <Stack space="medium">
                          <Text>
                            for Personal Information which you have received or
                            learnt directly from us, you must use and disclose
                            the Personal Information only:
                          </Text>
                          <List type="roman">
                            <Text>
                              to the extent necessary for the proper performance
                              and integration of the SEEK API; or
                            </Text>
                            <Text>with our prior written consent;</Text>
                          </List>
                        </Stack>
                        <Text>
                          without limiting the effect of clauses 34a) and b),
                          not use, disclose, copy or modify the Personal
                          Information for your own purposes, including but not
                          limited to any database building, marketing,
                          advertising or promotional purposes;
                        </Text>
                        <Text>
                          comply with the Privacy Laws of the relevant
                          individual’s country as if you were an entity
                          regulated by such Privacy Laws;
                        </Text>
                        <Text>
                          not do anything or omit to do anything that it could
                          reasonably expect would cause any member of the SEEK
                          Group to breach its obligations under a Privacy Law;
                          and
                        </Text>
                        <Text>
                          comply with all our reasonable directions relating to
                          the protection of the Personal information and
                          compliance with the Privacy Laws, and cooperate fully
                          with us in the resolution of any complaint alleging a
                          breach of any Privacy Law.
                        </Text>
                      </List>
                    </Stack>
                    <Text>
                      You have, and maintain, a privacy policy in relation to
                      the Data collected by you that complies, in all material
                      respects, with the requirements of all applicable Privacy
                      Laws.
                    </Text>
                    <Text>
                      You have, and maintain your own user terms and privacy
                      policy, which must be readily accessible to the users of
                      your Recruitment Software.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem id="api-versioning" label="API Versioning">
                  <List type="number" start={37}>
                    <Text>
                      Any changes to the SEEK API shall be announced in the
                      Release notes section of the applicable SEEK Developer
                      Site. We will provide you with 30 days’ notice of any
                      changes that, if not implemented, will break your
                      integration with the SEEK API.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem id="support" label="Support">
                  <List type="number" start={38}>
                    <Stack space="medium">
                      <Text>
                        Any issues impacting the Advertiser’s use of any of our
                        websites and apps via the SEEK API are to be advised to
                        us via the Contact Us form on the Partner Hub at{' '}
                        <TextLink href="/contact">
                          {useLocalized({
                            AU: 'https://talent.seek.com.au/contactus',
                            NZ: 'https://talent.seek.co.nz/contactus',
                          })}
                        </TextLink>{' '}
                        within 1 hour of the issue being detected. Support
                        requests must clearly describe:
                      </Text>
                      <List type="alpha">
                        <Text>
                          an assessment of the impact to the Advertiser;
                        </Text>
                        <Text>the cause of the issue (if known);</Text>
                        <Text>the duration of the issue;</Text>
                        <Text>
                          the expected timeframe to resolve the issue (if
                          known); and
                        </Text>
                        <Text>
                          any supporting logging data that can assist for
                          diagnostic purposes.
                        </Text>
                      </List>
                    </Stack>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="rights-reserved"
                  label="Rights reserved by us"
                >
                  <List type="number" start={39}>
                    <Text>
                      If you breach these Terms and fail to remedy such breach
                      within 14 days of us notifying you in writing, we may
                      restrict or terminate your access to the SEEK API.
                    </Text>
                    <Text>
                      We may control the use of the SEEK API by you at any time.
                    </Text>
                    <Text>
                      We may provide you with support or modifications for the
                      SEEK API, at our sole discretion.
                    </Text>
                    <Text>
                      We may stop providing support or modifications to you at
                      any time without notice or liability to you if we
                      reasonably consider that doing so is unlikely to be
                      detrimental to you. If withdrawing support or the
                      provision of modifications to you is reasonably likely to
                      be detrimental to you, we will notify you at least 30 days
                      prior to ceasing providing such support or modifications.
                      If you do not agree to the withdrawal of this support or
                      modifications, you may terminate these Terms by
                      discontinuing your use of the SEEK API with 5 days&apos;
                      written notice to us.
                    </Text>
                    <Text>
                      We may release subsequent versions or new features of the
                      SEEK API and require that you use those subsequent
                      versions or new features. Your continued use of the SEEK
                      API following a subsequent release of versions or features
                      will be deemed your acceptance of modifications.
                    </Text>
                    <Text>
                      For each functionality listed in Sections A to I below, we
                      may release subsequent versions of each functionality.
                      Where reasonably possible, we will announce version
                      changes at least 30 days in advance of its release to
                      allow you to prepare for any impact. You are required to
                      move to a supported version before the current version
                      reaches end of support date for the integration to remain
                      function. Your continued use of each functionality
                      following a subsequent release, will be deemed your
                      acceptance of the modifications.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem id="indemnity" label="Indemnity">
                  <List type="number" start={45}>
                    <Stack space="medium">
                      <Text>
                        You indemnify and will keep indemnified the SEEK Group,
                        its officers, employees and agents against all claims,
                        actions, suits, liabilities, actual or contingent costs,
                        damages and expenses incurred by us as a direct result
                        of:
                      </Text>
                      <List type="alpha">
                        <Text>any negligent act or omission by you; or</Text>
                        <Text>
                          an actual or alleged breach by you of any law,
                          legislation, regulations, by-laws, ordinances or codes
                          of conduct which occurs as a result of your use of the
                          SEEK API.
                        </Text>
                      </List>
                      <Text>
                        Each party&apos;s liability under these terms will be
                        reduced to the extent that the relevant loss or damage
                        was caused or contributed to by the other party.
                      </Text>
                    </Stack>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="limitation-of-liability"
                  label="Limitation of Liability"
                >
                  <List type="number" start={46}>
                    <Text>
                      Any program or content made available to you by the SEEK
                      Group on the Developer Site (including, without
                      limitation, the Data and the SEEK API) is provided to you
                      on an “as is” basis and we make no representations about
                      the accuracy, completeness, currency or suitability of
                      such content.
                    </Text>
                    <Text>
                      While we will use reasonable endeavours to ensure that the
                      SEEK API is accessible to your Recruitment Software at all
                      times (subject to standard maintenance), we make no
                      representation that the SEEK API will be available on a
                      continuous basis.
                    </Text>
                    <Text>
                      To the extent permitted by law, the SEEK Group disclaims
                      all liability for loss, damage, cost, claim or expense
                      (including legal expense) whether direct or indirect (such
                      as loss of profits, loss of opportunity and loss of data)
                      incurred by you or Advertisers due to your use of the SEEK
                      API, or the Data.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="term-and-termination"
                  label="Term and Termination"
                >
                  <List type="number" start={49}>
                    <Text>
                      The “<Strong>Term</Strong>” of these Terms commences from
                      the date which you agree to these Terms and will continue
                      until terminated in accordance with clause 50, 51 or 53,
                      set out below.
                    </Text>
                    <Stack space="medium">
                      <Text>
                        We reserve the right to, in our absolute discretion but
                        acting reasonably, immediately suspend your Licence if:
                      </Text>
                      <List type="alpha">
                        <Text>you breach the warranties in clause 31;</Text>
                        <Text>
                          you breach the privacy obligations in clause 34;
                        </Text>
                        <Text>you breach any applicable laws;</Text>
                        <Text>
                          SEEK reasonably believes that you pose a security
                          threat in any way (including in relation to candidates
                          or our systems); or
                        </Text>
                        <Text>
                          SEEK reasonably believes that you have caused, or are
                          likely to cause, damage to our brand or reputation.
                        </Text>
                      </List>
                    </Stack>
                    <Text>
                      Either party may terminate these Terms by written notice
                      if the other party materially breaches any of these Terms
                      and does not remedy such breach within 14 days of being
                      notified to do so.
                    </Text>
                    <Text>
                      Termination of our agreement with you as a result of you
                      breaching any one or more of these Terms, will not end
                      provisions of these Terms that are capable of surviving
                      termination.
                    </Text>
                    <Text>
                      Either party may terminate these Terms at any time by
                      providing the other party with 60 days&apos; prior written
                      notice.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem id="miscellaneous" label="Miscellaneous">
                  <List type="number" start={54}>
                    <Text>
                      Any written communications between you and us will be made
                      via the email address provided as part of your
                      Identification Details.
                    </Text>
                    <Text>
                      These Terms (including other terms referenced within, to
                      the extent relevant) constitute the entire agreement
                      between the parties and supersede all other
                      representations or agreements, whether written or oral,
                      between us relating to this matter.
                    </Text>
                    <Text>
                      We may assign or novate rights and obligations under these
                      Terms without your consent.
                    </Text>
                    <Text>
                      Any clause within these Terms which is wholly or partially
                      void or unenforceable is severed to the extent that it is
                      void or unenforceable. The validity or enforceability of
                      the remainder of these Terms is not affected.
                    </Text>
                    <Text>
                      These terms do not create a partnership, joint venture or
                      agency relationship between you and us and you will not
                      make any statement which suggests such a relationship. You
                      must not state that any member of the SEEK Group sponsors
                      or endorses your business or product without our prior
                      written approval.
                    </Text>
                    <Stack space="medium">
                      <Text>
                        If you are a third party provider accessing the SEEK API
                        to service Mutual Customers:
                      </Text>
                      <List type="alpha">
                        <Text>
                          your contractual relationship is with SEEK Limited
                          (ACN 080 075 314);
                        </Text>
                        <Text>
                          these Terms are governed by the laws of Victoria,
                          Australia. Each party irrevocably and unconditionally
                          submits to the exclusive jurisdiction of the courts of
                          Victoria, Australia; and
                        </Text>
                        <Text>
                          SEEK Limited reserves the right to assign or novate
                          these Terms (or any aspect of these Terms) to any
                          member of the SEEK Group without your consent. If this
                          occurs, we may elect to amend the details in clause 59
                          a). We will notify you of such changes 30 days in
                          advance.
                        </Text>
                      </List>
                    </Stack>
                    <Text>
                      If you are an Advertiser that accesses the SEEK API
                      directly (i.e. you do not use a third party provider),
                      these Terms are incorporated into the{' '}
                      <TextLink href="/support/termsconditions">
                        Advertising Terms of Use
                      </TextLink>
                      , which also apply to you. This means that the provisions
                      of the{' '}
                      <TextLink href="/support/termsconditions">
                        Advertising Terms of Use
                      </TextLink>{' '}
                      (including those relating to contracting parties and
                      governing law) also apply to these Terms. In the event of
                      any conflict, these Terms prevail.{' '}
                    </Text>
                    <Text>
                      Any member of the SEEK Group may assist in providing the
                      relevant products and services to you.{' '}
                    </Text>
                  </List>
                </AccordionItem>
              </Accordion>
              <Box paddingTop="gutter">
                <Text weight="strong">
                  <em>
                    The sections below may be applicable to you depending on the
                    types of functionality you have chosen through the SEEK API.
                    These sections form part of the Terms:
                  </em>
                </Text>
              </Box>
              <Accordion>
                <AccordionItem
                  id="section-a"
                  label="Section A: Talent Search Connect"
                >
                  <Stack space="gutter">
                    <List type="number" start={62}>
                      <Text>
                        Section A applies if you have chosen the Talent Search
                        Connect functionality through the SEEK API.
                      </Text>
                      <Stack space="medium">
                        <Text>In this Section A:</Text>
                        <List type="alpha">
                          <Text>
                            <Strong>Defect</Strong> means a failure of the
                            Talent Search Connect integration to operate in
                            accordance with our requirements as set out in the
                            Proactive Sourcing section of the SEEK Developer
                            Site.
                          </Text>
                          <Text>
                            <Strong>Storybook</Strong> is a frontend workshop
                            for building user interface components.
                          </Text>
                          <Text>
                            <Strong>Widget</Strong> means the features or
                            elements of the Talent Search Connect experience
                            that we require you to display in your Recruitment
                            Software in connection with making Talent Search
                            Connect available to Mutual Customers.
                          </Text>
                        </List>
                      </Stack>
                    </List>
                    <Heading level="4">Functional</Heading>
                    <List type="number" start={64}>
                      <Text>
                        Within 72 hours of us notifying you that a Mutual
                        Customer has purchased access to Talent Search Connect,
                        you must enable Talent Search Connect for the Mutual
                        Customer.
                      </Text>
                      <Stack space="medium">
                        <Text>
                          At all times, you must ensure that the following
                          Talent Search Connect functionality is enabled for
                          Mutual Customers:
                        </Text>
                        <List type="alpha">
                          <Stack space="medium">
                            <Text>
                              all applicable Widgets are embedded in visible,
                              logical locations in the Mutual Customer’s hiring
                              workflow within your user interface, including,
                              but not limited to:
                            </Text>
                            <List type="roman">
                              <Text>the Candidate’s Profile;</Text>
                              <Text>
                                job requisition creation and job management; and
                              </Text>
                              <Text>
                                global navigation to the Talent Search Connect
                                entry point;
                              </Text>
                            </List>
                          </Stack>
                          <Text>
                            the ability to share Mutual Customer’s active/open
                            jobs within your Recruitment Software (irrespective
                            of whether it is advertised with us) via the SEEK
                            API;
                          </Text>
                          <Text>
                            the ability to share Mutual Customer’s Candidate
                            Profiles and previous interactions with these
                            Candidates via the SEEK API;
                          </Text>
                          <Text>
                            that shared job, Candidate and interaction data is
                            made available to us within 60 seconds of the change
                            taking place in your Recruitment Software;
                          </Text>
                          <Text>
                            the importing of a Candidate Profile or resume
                            purchased from Talent Search Connect into your
                            Recruitment Software and, where position data is
                            included as part of the Profile purchase payload,
                            the appropriate job; and
                          </Text>
                          <Text>
                            that purchased Profiles must be viewable by the
                            Mutual Customer in your Recruitment Software within
                            60 seconds of receiving a purchase event
                            notification from us.
                          </Text>
                        </List>
                      </Stack>
                    </List>
                    <Heading level="4">Messaging / Branding</Heading>
                    <List type="number" start={66}>
                      <Text>
                        You must ensure that all Widgets are placed in easily
                        discoverable locations within the Mutual Customer’s
                        hiring workflow with respect to the context of each
                        Widget’s individual functionality. We will provide
                        general guidance for appropriate locations for each
                        Widget in the Proactive Sourcing section of the SEEK
                        Developer Site.
                      </Text>
                      <Text>
                        You must ensure that each Widget’s minimum size as
                        specified in Storybook is respected and is not obscured
                        or obstructed by other elements in your user interface.
                      </Text>
                      <Text>
                        You must promptly notify us if you make any significant
                        changes to the user interface that may affect the Talent
                        Search Connect functionality and the positioning of any
                        Widgets.
                      </Text>
                      <Text>
                        We may at any time update or change the content of the
                        Talent Search Connect Widgets at its own discretion.
                      </Text>
                      <Text>
                        Upon successfully integrating Talent Search Connect with
                        your Recruitment Software, we will review your
                        implementation to ensure it appropriately represents our
                        brand and value proposition. We may review the
                        implementation from time to time and require you to fix
                        any Defects found within 30 days of being notified. We
                        may decide to end your integration with Talent Search
                        Connect if it determines, at our sole discretion, that
                        the implementation by you does not appropriately
                        represent our brand and value proposition.
                      </Text>
                    </List>
                    <Heading level="4">Offboarding</Heading>
                    <List type="number" start={71}>
                      <Stack space="medium">
                        <Text>
                          Within 15 days of us notifying you that a Mutual
                          Customer no longer has access to Talent Search Connect
                          you must work with us to offboard the Mutual Customer,
                          including to:
                        </Text>
                        <List type="alpha">
                          <Text>
                            disable the Talent Search Connect integration;
                          </Text>
                          <Text>
                            remove any available Widgets from your Recruitment
                            Software; and
                          </Text>
                          <Text>
                            cease sending us any applicable Mutual Customer
                            data.
                          </Text>
                        </List>
                      </Stack>
                    </List>
                  </Stack>
                </AccordionItem>

                <AccordionItem id="section-b" label="Section B: Job Posting">
                  <Stack space="gutter">
                    <Heading level="4">Functional</Heading>
                    <List type="number" start={72}>
                      <Text>
                        Section B applies if you have chosen to use the job
                        posting functionality through the SEEK API and we
                        reserve the right to request that you change or rectify
                        any job posting functionality at any time.
                      </Text>
                      <Stack space="medium">
                        <Text>
                          You must ensure that the following job posting
                          functionality is enabled for Advertisers via the SEEK
                          API at all times:
                        </Text>
                        <List type="alpha">
                          <Text>
                            the ability for an Advertiser to create, update and
                            expire an ad;
                          </Text>
                          <Text>
                            the ability for the Advertiser to use multiple
                            Advertiser accounts with us;
                          </Text>
                          <Text>
                            enable and display all SEEK Group ad products via
                            the Ad Selection Panel or Ad Selection with GraphQL
                            feature;
                          </Text>
                          <Text>
                            the ability for an Advertiser to specify the
                            location of a position supported by the SEEK APAC
                            hierarchy when posting a job ad;
                          </Text>
                          <Text>
                            the ability for an Advertiser to specify the job
                            category of a position supported by the SEEK APAC
                            hierarchy when posting a job ad;
                          </Text>
                          <Text>
                            the ability for an Advertiser to select SEEK Group
                            approved branding when posting a job ad;
                          </Text>
                          <Text>
                            the ability for an Advertiser to specify the right
                            salary frequency and choose the right currency from
                            the list of our supported currencies when specifying
                            salary information;
                          </Text>
                          <Text>
                            distribute the live link of the posted job ad to the
                            Advertiser;
                          </Text>
                          <Text>
                            display appropriate messaging to the Advertiser if
                            an error occurs when posting a job; and
                          </Text>
                          <Text>
                            the ability for an Advertiser to post an ad on our
                            websites and apps for 30 days.
                          </Text>
                        </List>
                      </Stack>
                    </List>
                  </Stack>
                </AccordionItem>

                <AccordionItem
                  id="section-c"
                  label="Section C: Ad Selection Panel"
                >
                  <Stack space="gutter">
                    <Heading level="4">Functional</Heading>
                    <List type="number" start={74}>
                      <Text>
                        Section C applies if you have chosen to use the Panel
                        functionality through the SEEK API.
                      </Text>
                      <Text>
                        <Strong>Panel</Strong> means an Ad Selection Panel which
                        is an embeddable widget that you can place in your
                        posting flow to retrieve available ad products during
                        advertisement creation and modification.
                      </Text>
                      <Stack space="medium">
                        <Text>You acknowledge and agree that:</Text>
                        <List type="alpha">
                          <Text>
                            if you choose to utilise the Panel, it must be made
                            available to all Advertisers. Removal of the Panel
                            for any Advertiser must be discussed and agreed with
                            us beforehand;
                          </Text>
                          <Text>
                            all SEEK Group products available through the Panel
                            must be visible and accessible to all Advertisers;
                          </Text>
                          <Text>
                            you will make reasonable endeavours to load the
                            Panel on the page within 1 second;
                          </Text>
                          <Stack space="medium">
                            <Text>
                              pricing information surfaced via the Panel is not
                              to be used for any other purpose other than for
                              displaying:
                            </Text>
                            <List type="roman">
                              <Text>
                                the price of our products to Advertisers who are
                                posting job ads in the flow;
                              </Text>
                              <Text>job posting spend analysis, and</Text>
                              <Text>
                                other relevant reporting and management
                                information.
                              </Text>
                            </List>
                          </Stack>
                        </List>
                      </Stack>
                    </List>
                    <Heading level="4">Messaging / Branding</Heading>
                    <List type="number" start={77}>
                      <Text>
                        You must not change or modify any design elements
                        related to the Panel, unless we have provided consent
                        for you to do so.
                      </Text>
                      <Text>
                        If you believe an element of the design is sub-optimal
                        for your users, you may request a design change by
                        contacting us, which we will consider in our sole
                        discretion.
                      </Text>
                    </List>
                  </Stack>
                </AccordionItem>

                <AccordionItem
                  id="section-d"
                  label="Section D: Ad Selection with GraphQL"
                >
                  <Stack space="gutter">
                    <Heading level="4">Functional</Heading>
                    <List type="number" start={79}>
                      <Text>
                        Section D applies if you have chosen to use the Ad
                        Selection with GraphQL functionality through the SEEK
                        API.
                      </Text>
                      <Text>
                        <Strong>Ad Selection with GraphQL</Strong> means the
                        method used to programmatically retrieve available ad
                        products by your backend during advertisement creation
                        and modification.
                      </Text>
                      <Stack space="medium">
                        <Text>You acknowledge and agree that:</Text>
                        <List type="alpha">
                          <Text>
                            if you choose to utilise the Ad Selection with
                            GraphQL functionality, it must be available to all
                            Advertisers. Removal of Ad Selection with GraphQL
                            for any Advertiser must be discussed and agreed with
                            us beforehand;
                          </Text>
                          <Text>
                            all SEEK Group products available through the Ad
                            Selection with GraphQL must be and accessible to all
                            Advertisers; and
                          </Text>
                          <Stack space="medium">
                            <Text>
                              pricing information surfaced via Ad Selection with
                              GraphQL is not to be used for any other purpose
                              other than for displaying:
                            </Text>
                            <List type="roman">
                              <Text>
                                the price of our products to Advertisers who are
                                posting job ads in the flow;
                              </Text>
                              <Text>job posting spend analysis, and</Text>
                              <Text>
                                other relevant reporting and management
                                information.
                              </Text>
                            </List>
                          </Stack>
                        </List>
                      </Stack>
                    </List>
                    <Heading level="4">Messaging / Branding</Heading>
                    <List type="number" start={82}>
                      <Text>
                        Upon successfully integrating Ad Selection with GraphQL
                        into the partner system, we will review the
                        implementation to ensure the design appropriately
                        represents our brand and value proposition. We may
                        decide to end your integration with the SEEK API if we
                        determine, in our sole discretion, that the design
                        implemented by you does not appropriately represent our
                        brand and value proposition.
                      </Text>
                    </List>
                  </Stack>
                </AccordionItem>

                <AccordionItem
                  id="section-e"
                  label="Section E: Questionnaire Panel"
                >
                  <Stack space="gutter">
                    <Heading level="4">Functional</Heading>
                    <List type="number" start={83}>
                      <Text>
                        Section E applies if you have chosen to use the
                        Questionnaire Panel functionality through the SEEK API.
                      </Text>
                      <Text>
                        <Strong>Questionnaire Panel</Strong> means our
                        questionnaire panel that you can embed into your posting
                        flow to select and create questions to assign to a job
                        ad during advertisement creation.
                      </Text>
                      <Stack space="medium">
                        <Text>You acknowledge and agree that:</Text>
                        <List type="alpha">
                          <Text>
                            If you choose to use the Questionnaire Panel, it
                            must be made available to all Advertisers. Removal
                            of this panel for any Advertiser must be discussed
                            and agreed with us beforehand;
                          </Text>
                          <Text>
                            if you have chosen to embed the Questionnaire Panel,
                            you agree to use these questions only for their
                            intended purpose;
                          </Text>
                          <Text>
                            within the Questionnaire Panel, all our questions
                            returned as a result of the query to retrieve
                            questions from us must be visible and selectable for
                            the Advertiser posting the job ad; and
                          </Text>
                          <Text>
                            any questions created as part of the advertisement
                            creation that are not selected from our existing
                            library of questions must adhere to applicable
                            Privacy Laws and these Terms.
                          </Text>
                        </List>
                      </Stack>
                    </List>
                    <Heading level="4">Messaging / Branding</Heading>
                    <List type="number" start={86}>
                      <Text>
                        You must not change or modify any design elements
                        related to the Questionnaire Panel, unless we have
                        provided consent for you to do so.
                      </Text>
                      <Text>
                        If you believe an element of the design is sub-optimal
                        for your users, you may request a design change by
                        contacting us, which we will consider in our sole
                        discretion.
                      </Text>
                    </List>
                  </Stack>
                </AccordionItem>

                <AccordionItem
                  id="section-f"
                  label="Section F: Questionnaire Panel with GraphQL"
                >
                  <Stack space="gutter">
                    <Heading level="4">Functional</Heading>
                    <List type="number" start={88}>
                      <Text>
                        Section F applies if you have chosen to use the Custom
                        Questionnaire Panel functionality through the SEEK API.
                      </Text>
                      <Text>
                        <Strong>Custom Questionnaire Panel</Strong> means the
                        panel implementation used to programmatically create
                        questionnaires by your backend via GraphQL during
                        advertisement creation.
                      </Text>
                      <Stack space="medium">
                        <Text>You acknowledge and agree that:</Text>
                        <List type="alpha">
                          <Text>
                            if you choose to use the Custom Questionnaire Panel,
                            it must be made available to all Advertisers.
                            Removal of this panel for any Advertiser must be
                            discussed and agreed with us beforehand;
                          </Text>
                          <Text>
                            if you have chosen to embed the Custom Questionnaire
                            Panel, you agree to use these questions only for
                            their intended purpose; and
                          </Text>
                          <Text>
                            within the Custom Questionnaire Panel, all our
                            questions returned as a result of the query to
                            retrieve questions from us must be visible and
                            selectable for the hirer posting the job ad.
                          </Text>
                        </List>
                      </Stack>
                    </List>
                    <Heading level="4">Messaging / Branding</Heading>
                    <List type="number" start={91}>
                      <Text>
                        Upon successfully integrating the Custom Questionnaire
                        Panel into your system, we will review the
                        implementation to ensure the design appropriately
                        represents our brand and value proposition. We may
                        decide to end your integration with the SEEK API if it
                        determines in our sole discretion, that the design
                        implemented by you does not appropriately represent our
                        brand and value proposition.
                      </Text>
                    </List>
                  </Stack>
                </AccordionItem>

                <AccordionItem id="section-g" label="Section G: ATS Questions">
                  <Stack space="gutter">
                    <Heading level="4">Functional</Heading>
                    <List type="number" start={92}>
                      <Text>
                        Section G applies if you have chosen to use ATS
                        Questions functionality through the SEEK API.
                      </Text>
                      <Text>
                        <Strong>ATS Questions</Strong> means questionnaire
                        creation using questions from your Recruitment Software
                        in the structure expected by the SEEK API, which returns
                        an ID that can be specified when posting a job ad.
                      </Text>
                      <Text>
                        You acknowledge and agree that any questions created as
                        part of the advertisement creation that are not selected
                        from our existing library of questions must adhere to
                        applicable Privacy Laws and these Terms.
                      </Text>
                    </List>
                    <Heading level="4">Messaging / Branding</Heading>
                    <List type="number" start={95}>
                      <Text>
                        Upon successfully integrating the ATS Questions into the
                        partner system, we will review the implementation to
                        ensure the design appropriately represents our brand and
                        value proposition. We may decide to end your integration
                        with the SEEK API if it determines, in our sole
                        discretion, that the design implemented by you does not
                        appropriately represent our brand and value proposition.
                      </Text>
                    </List>
                  </Stack>
                </AccordionItem>

                <AccordionItem
                  id="section-h"
                  label="Section H: Optimised Apply Solution"
                >
                  <List type="number" start={96}>
                    <Text>
                      Section H applies if you have chosen to use the Optimised
                      Apply Solution functionality through the SEEK API.
                    </Text>
                    <Text>
                      <Strong>Optimised Apply Solution</Strong> means the method
                      used to distribute Candidate applications to Partners and
                      Advertisers.
                    </Text>
                    <Text>
                      You must ensure that the following Optimised Apply
                      Solution functionality is enabled for Advertisers via the
                      SEEK API at all times: retrieving and distributing
                      Candidate applications to the Advertiser including all
                      associated documents. Associated documents may include
                      resumes, cover letters, Profiles, submission to
                      questionnaires.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="section-i"
                  label="Section I: Apply with SEEK"
                >
                  <List type="number" start={99}>
                    <Text>
                      Section I applies if you have chosen to use the Apply with
                      SEEK Solution functionality through the SEEK API.
                    </Text>
                    <Text>
                      <Strong>Apply with SEEK Solution</Strong> means the method
                      used to distribute Candidate applications to you and
                      Advertisers, using information from a Candidate’s Profile
                      to pre-fill the application.
                    </Text>
                    <Text>
                      You must ensure that the following Apply with SEEK
                      Solution functionality is enabled for Advertisers on all
                      their job ads via the SEEK API at all times: pre-filling
                      the application on the Advertiser’s application form with
                      information from a Candidate’s Profile.
                    </Text>
                    <Text>
                      Apply with SEEK Solution functionality must only be used
                      for pre-filling Candidates’ job application forms and must
                      not be used for any other purposes such as user account
                      registrations.
                    </Text>
                    <Text>
                      If you are building Apply with SEEK Solution, you are
                      required to send us completion signals for job ads where
                      Apply with SEEK is enabled irrespective of whether
                      candidates use Apply with SEEK or not.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="section-j"
                  label="Section J: Ad Performance Panel"
                >
                  <List type="number" start={104}>
                    <Text>
                      Section J applies if you have chosen to use the Ad
                      Performance Panel functionality through the SEEK API.
                    </Text>
                    <Text>
                      <Strong>Ad Performance Panel</Strong> means the widget
                      that can be embedded within your Recruitment Software that
                      will show the job search views, job ad clicks, application
                      starts and application completes for each SEEK job ad. It
                      can also provide insights such as market performance,
                      benchmarked against job ads posted with similar job titles
                      and locations.
                    </Text>
                    <Text>
                      If you are building the Ad Performance Panel, you are
                      required to send us completion signals for each
                      application submitted by candidates arriving at your apply
                      form from SEEK, irrespective of whether they use Apply
                      with SEEK or not.
                    </Text>
                    <Text>
                      Information shown in the Ad Performance Panel must not be
                      extracted and used outside of Ad Performance Panel.
                    </Text>
                  </List>
                </AccordionItem>

                <AccordionItem
                  id="section-k"
                  label="Section K: Selection Signals"
                >
                  <Stack space="medium">
                    <List type="number" start={108}>
                      <Text>
                        Section K applies if you have chosen to use the
                        Selection Signals functionality through the SEEK API.
                      </Text>
                      <Text>
                        <Strong>Selection Signals</Strong> means the
                        functionality used by you to send us the Application
                        Signals and Position Signals in respect of a job ad
                        posted by an Advertiser via the SEEK API.
                      </Text>
                      <Stack space="medium">
                        <Text>In this Section K:</Text>
                        <List type="alpha">
                          <Text>
                            <Strong>Application Signals</Strong> are the updates
                            made by an Advertiser to a candidate’s application
                            status, within your Recruitment Software, as the
                            candidate progresses through an Advertiser’s
                            recruitment process. This could include, but is not
                            limited to, statuses such as ‘Shortlisted’,
                            ‘Interview’, ‘Hired’ and ‘Rejected’ (or equivalent
                            terms).
                          </Text>
                          <Text>
                            <Strong>Position Signals</Strong> are the updates
                            made by an Advertiser to the job requisition status,
                            within your Recruitment Software, throughout an
                            Advertiser’s recruitment process. This could
                            include, but is not limited to, ‘Advertising’,
                            ‘Filled’ and On Hold’ (or equivalent terms).
                          </Text>
                        </List>
                      </Stack>
                      <Stack space="medium">
                        <Text>
                          You must ensure that the manner in which the
                          Application Signals and Position Signals are provided
                          to us meets the following criteria:
                        </Text>
                        <List type="alpha">
                          <Text>
                            The Application Signals and Positions Signals are
                            provided in an acceptable format as agreed with us.
                          </Text>
                          <Text>
                            The signal delivery of the Application Signals and
                            Position Signals are sent promptly to us whenever
                            there is an update by an Advertiser to any status.
                            If near real time delivery is not possible, signal
                            delivery can be provided on a scheduled daily basis.
                          </Text>
                          <Text>
                            You have implemented a mechanism for retrying signal
                            transmission of the Application Signals and Position
                            Signals until delivery to us is successful.
                          </Text>
                        </List>
                      </Stack>
                    </List>
                    <Stack space="medium">
                      <Heading level="4">
                        Onboarding/Opt-out Functionality
                      </Heading>
                      <List type="number" start={112}>
                        <Text>
                          Before utilising the Selection Signals functionality,
                          you must provide prior written notice to Advertisers
                          that use your Recruitment Software confirming that
                          they will be opted-in to share Application and
                          Position Signals with SEEK (
                          <Strong>Written Notice</Strong>) unless they opt out.
                        </Text>
                        <Stack space="medium">
                          <Text>The Written Notice must:</Text>
                          <List type="alpha">
                            <Text>
                              confirm the date upon which the Application
                              Signals and Position Signals will commence being
                              shared with us;
                            </Text>
                            <Text>
                              confirm that the Application Signals and Position
                              Signals will be collected and managed by SEEK in
                              accordance with SEEK’s Privacy Policy (
                              <TextLink href="https://www.seek.com.au/privacy">
                                https://www.seek.com.au/privacy
                              </TextLink>
                              ); and
                            </Text>
                            <Text>
                              provide clear information as to how an Advertiser
                              can opt out of sharing Application Signals and
                              Position Signals (<Strong>Opt Out</Strong>).
                            </Text>
                          </List>
                        </Stack>
                        <Text>
                          The Selection Signals Opt Out is your responsibility
                          to manage and must be made available to Advertisers at
                          all times. In the event that an Advertiser elects to
                          opt out of this functionality, you must immediately
                          cease providing Application Signals and Position
                          Signals of that Advertiser to SEEK.
                        </Text>
                        <Text>
                          We reserve the right to verify your compliance with
                          this section at any time.
                        </Text>
                      </List>
                    </Stack>
                  </Stack>
                </AccordionItem>
              </Accordion>
            </Stack>
          </Stack>
        </ContentBlock>
      </Box>
    </PageWrapper>
  );
};
